import React from "react";
import { Button, Tooltip, Typography, Box } from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { getTypeColor } from "../../../../constants/colors";

/**
 * InquiryButton
 *
 * 「詳細を聞きたい」などの問い合わせボタンを、スマホ・PCで分岐表示する共通コンポーネント。
 * - PC：ボタンをhoverでTooltipを表示
 * - スマホ：ボタン下に説明テキストを表示
 * - ボタンラベル、ツールチップ文言、色味(sx)はpropsでカスタマイズ可
 *
 * @param {boolean} isMobile - モバイル判定
 * @param {function} onClick - ボタン押下時の処理
 * @param {string} label - ボタンのテキスト
 * @param {string} tooltip - Tooltipや説明テキスト
 * @param {object} buttonSx - ボタンのカスタムスタイル
 * @param {React.ReactNode} icon - ボタンに表示するアイコン
 */
const InquiryButton = ({
  isMobile,
  onClick,
  label = "詳細を聞きたい",
  tooltip = "企業に問い合わせメールが送信されます",
  type = "project",
  buttonSx = {},
  icon,
}) => {
  // job/project 用の共通設定
  const baseColor = getTypeColor(type);

  // デフォルトアイコン
  const iconNode = icon || <MailOutlineIcon sx={{ fontSize: 16 }} />;

  // ボタンの中身（共通）
  const ButtonInner = (
    <Box
      sx={{
        position: "relative",
        height: 40,
        borderRadius: "999px",

        pr: { xs: 3.5, sm: 6 },
        pl: { xs: 5.5, sm: 8 },

        minWidth: { sm: 220 },
        width: { xs: "100%", sm: 240 },

        color: "#fff",
        fontWeight: 600,
        fontSize: "0.9rem",
        boxShadow: 4,
        transition: "all 0.15s ease",

        // テキストを中央寄せ
        display: "flex",
        alignItems: "center",
        justifyContent: "center",

        "&:hover": {
          boxShadow: "none",
        },
      }}
    >
      {/* 左のアイコン丸 */}
      <Box
        sx={{
          position: "absolute",
          left: 6,
          width: 30,
          height: 30,
          borderRadius: "50%",
          backgroundColor: baseColor,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {iconNode}
      </Box>

      {/* ボタンラベル */}
      <Typography
        sx={{
          fontSize: "0.85rem",
          fontWeight: 600,
          color: baseColor,
        }}
      >
        {label}
      </Typography>
    </Box>
  );

  //枠付きボタン本体
  const BaseButton = (
    <Button
      onClick={onClick}
      sx={{
        width: isMobile ? "100%" : "auto",
        borderRadius: 9999,
        backgroundColor: "#fff",
        border: `2px solid ${baseColor}`,
        minHeight: "unset",
        p: 0,
        "&:hover": {
          backgroundColor: "#fff",
          opacity: 0.9,
        },
        ...buttonSx,
      }}
    >
      {ButtonInner}
    </Button>
  );

  // スマホとPCで出し分け
  return isMobile ? (
    <>
      {BaseButton}
      {/* スマホは下に補足説明を表示 */}
      <Typography fontSize="10px" color="#fff" mt={0.5} textAlign="center">
        {tooltip}
      </Typography>
    </>
  ) : (
    // PCはTooltipで説明表示
    <Tooltip title={tooltip} placement="top" arrow>
      <span>{BaseButton}</span>
    </Tooltip>
  );
};

export default InquiryButton;
